/*
 * @Author: Gang Jiang 
 * @Date: 2024-09-25 19:24:36 
 * @Last Modified by: Gang Jiang
 * @Last Modified time: 2024-10-08 16:29:12
 */

<template>
  <div class="affiliate-detail">
    <div
      v-for="(affiliate, index) in affiliateList"
      :key="index"
      :class="
        'affiliate-item' +
          (index == showAffiliateIndex ? ' active' : '') +
          (index == showAffiliateIndex + 1 ? ' top-border' : '')
      "
    >
      <div class="summary">
        <div class="left">
          <label class="name">{{ affiliate.affiliate.name }}</label>
          <p>
            Status:
            <em :class="'value status ' + affiliate.status">{{
              affiliate.status
            }}</em>
          </p>
        </div>
        <div class="right">
          Last Update Time:
          <em class="value">{{
            moment(affiliate.updateTime).format('YYYY-MM-DD HH:mm:ss')
          }}</em>
          by
          <a
            class="editor-name"
            href="javascript:;"
            @click="emitEvent(affiliate.id)"
          >{{ affiliate?.updateEditor?.name }}</a><a
            class="edit"
            :href="'/#/affiliate/edit/' + advertiserId + '?id=' + affiliate.id"
          >Edit</a>
          <i
            v-if="affiliateList.length > 1"
            :class="'expand' + (index == showAffiliateIndex ? ' sub' : '')"
            @click="setShowAffiliateIndex(index)"
          >{{ index == showAffiliateIndex ? '-' : '+' }}</i>
        </div>
      </div>
      <div
        v-if="showAffiliateIndex === index"
        class="affiliate-info"
      >
        <a-row :gutter="24">
          <a-col
            :span="8"
          >
            Affiliate: <em>{{ affiliate?.affiliate?.name }}</em>
          </a-col>
          <a-col
            :span="8"
          >
            Affiliate PID: <em>{{ affiliate?.affiliate?.pid }}</em>
          </a-col>
          <a-col
            :span="8"
          >
            Create Time:
            <em>{{
              moment(affiliate.createTime).format('YYYY-MM-DD HH:mm:ss')
            }}</em>
          </a-col>
        </a-row>
        <a-row :gutter="24">
          <a-col :span="24" class="converter-info-col">
            Converter Info:
            <em class="text-wrap">{{ affiliate.converterInfo || '-' }}</em>
          </a-col>
        </a-row>
        <a-row :gutter="24">
          <a-col
            :span="8"
          >
            Affiliate MID: <em>{{ affiliate.affiliateMid || '-' }}</em>
          </a-col>
          <a-col
            :span="8"
          >
            Affiliate Region:
            <em>{{ affiliate?.affiliateRegion?.name || '-' }}</em>
          </a-col>
        </a-row>
        <a-row :gutter="24">
          <a-col
            :span="24"
          >
            Note: <em>{{ affiliate.note || 'None' }}</em>
          </a-col>
        </a-row>
        <a-row :gutter="24">
          <a-col :span="24">
            KOL rule:
            <em>{{ getKolRuleText(affiliate) }}</em>
          </a-col>
        </a-row>
        <a-row :gutter="24">
          <a-col :span="8">
            Affiliate Threshold:
            <em>{{ getSalesThreshold(affiliate).scopeEnabled ? 'Yes' : 'No' }}</em>
          </a-col>
          <template v-if="getSalesThreshold(affiliate).scopeEnabled">
            <a-col :span="8">
              Monthly Limit:
              <em>{{
                getSalesThreshold(affiliate).monthlyLimit != null
                  ? `$${getSalesThreshold(affiliate).monthlyLimit}`
                  : '-'
              }}</em>
            </a-col>
            <a-col :span="8">
              Daily Limit:
              <em>{{
                getSalesThreshold(affiliate).dailyLimit != null
                  ? `$${getSalesThreshold(affiliate).dailyLimit}`
                  : '-'
              }}</em>
            </a-col>
          </template>
        </a-row>
        <a-row :gutter="24">
          <a-col :span="24">
            Influencer Threshold:
            <em>{{ getSalesThreshold(affiliate).influencerEnabled ? 'Yes' : 'No' }}</em>
          </a-col>
        </a-row>
        <a-row
          v-if="getSalesThreshold(affiliate).influencerEnabled"
          :gutter="24"
        >
          <a-col :span="24">
            <a-table
              class="influencer-threshold-table"
              size="small"
              :pagination="false"
              :columns="influencerThresholdColumns"
              :data-source="getSalesThreshold(affiliate).items"
              row-key="influencerId"
            >
              <template #bodyCell="{ column, record }">
                <template v-if="column.key === 'influencerName'">
                  {{
                    record.influencerName ||
                      record.userName ||
                      record.name ||
                      record.influencerId ||
                      '-'
                  }}
                </template>
                <template v-else-if="column.key === 'dailyLimit'">
                  {{ formatLimitAmount(record.dailyLimit) }}
                </template>
                <template v-else-if="column.key === 'monthlyLimit'">
                  {{ formatLimitAmount(record.monthlyLimit) }}
                </template>
              </template>
            </a-table>
          </a-col>
        </a-row>
        <a-row :gutter="24">
          <a-col
            :span="24"
          >
            Commission Overview:
            <em>{{ affiliate.commissionsOverview || '-' }}</em>
          </a-col>
        </a-row>
      </div>
      <CommissionDetail
        v-if="showAffiliateIndex === index && affiliate.commissions?.length"
        :commission-list="affiliate.commissions"
      />
    </div>
  </div>
</template>

<script setup>
import moment from 'moment'
import { ref, toRaw } from 'vue'
import CommissionDetail from '../components/commissionDetail.vue'

const props = defineProps({
  advertiserId: { type: [String, Number], default: undefined },
  affiliateList: { type: Array, default: () => [] },
})
const showAffiliateIndex = ref(-1)
const emit = defineEmits(['setShowAffiliateLogId'])
const affiliateList = toRaw(props.affiliateList)

const influencerThresholdColumns = [
  { title: 'Influencer', dataIndex: 'influencerName', key: 'influencerName' },
  { title: 'DailyLimit', dataIndex: 'dailyLimit', key: 'dailyLimit' },
  { title: 'MonthlyLimit', dataIndex: 'monthlyLimit', key: 'monthlyLimit' },
]

function getSalesThreshold(affiliate) {
  const raw = affiliate && affiliate.salesThreshold
  if (!raw || typeof raw !== 'object') {
    return {
      scopeEnabled: false,
      influencerEnabled: false,
      dailyLimit: null,
      monthlyLimit: null,
      items: [],
    }
  }
  return {
    scopeEnabled: !!raw.scopeEnabled,
    influencerEnabled: !!raw.influencerEnabled,
    dailyLimit: raw.dailyLimit ?? null,
    monthlyLimit: raw.monthlyLimit ?? null,
    items: Array.isArray(raw.items) ? raw.items : [],
  }
}

function getKolRuleText(affiliate) {
  if (!affiliate) return '-'
  if (!affiliate.kolRule) return 'No'
  const mode = String(affiliate.kolRuleMode || affiliate.ruleMode || '').toLowerCase()
  const rawList =
    mode === 'blacklist' ? affiliate.kolBlackList : affiliate.kolWhiteList
  const list = Array.isArray(rawList) ? rawList : []
  const names = list
    .map((kol) => {
      if (kol == null || typeof kol !== 'object') return ''
      return kol.name || kol.userName || ''
    })
    .filter(Boolean)
  const modeLabel =
    mode === 'blacklist' ? 'blackList' : mode === 'whitelist' ? 'whiteList' : mode
  if (names.length) {
    return `Yes (${modeLabel || 'list'}: ${names.join(', ')})`
  }
  return modeLabel ? `Yes (${modeLabel})` : 'Yes'
}

function formatLimitAmount(value) {
  if (value == null || value === '') return '-'
  const num = Number(value)
  if (Number.isNaN(num)) return String(value)
  return `$ ${num.toFixed(2)}`
}

if (affiliateList.length > 1) {
  for (var i = 0, len = affiliateList.length; i < len; i++) {
    if (affiliateList[i].status === 'active') {
      showAffiliateIndex.value = i
      break
    }
  }
} else {
  showAffiliateIndex.value = 0
}

const setShowAffiliateIndex = (i) => {
  showAffiliateIndex.value = showAffiliateIndex.value === i ? -2 : i
}
const emitEvent = (id) => {
  emit('setShowAffiliateLogId', id)
}
</script>

<style lang="less" scoped>
.affiliate-detail {
  margin-left: 180px;
}
.summary {
  display: flex;
  color: #999;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e8e8e8;
  .left {
    display: flex;
    align-items: center;
  }
  .name {
    color: #000;
    font-size: 20px;
    margin: 10px 20px 10px 0;
  }
  .value {
    color: #000;
    font-style: normal;
    display: inline-block;
  }
  .expand {
    width: 30px;
    height: 30px;
    cursor: pointer;
    font-size: 20px;
    line-height: 30px;
    margin-left: 10px;
    font-style: normal;
    text-align: center;
    display: inline-block;
    &.sub {
      // '-'
      font-size: 24px;
    }
  }
  .edit {
    color: #1670ff;
    margin-left: 20px;
    text-decoration: none;
    &or-name {
      color: #1670ff;
      text-decoration: none;
    }
  }
  a:hover {
    color: #f00;
  }
}
.affiliate-item {
  border-bottom: 1px solid #ccc;
  &.top-border {
    border-top: 1px solid #ccc;
  }
  .summary {
    border-bottom: none;
  }
  &.active {
    border-bottom: none;
    .summary {
      border-bottom: 1px solid #e8e8e8;
    }
  }
  :deep(.commission-component) {
    .ant-table-row:last-of-type > td {
      border-bottom: none;
    }
  }
  &:last-of-type {
    :deep(.commission-component) {
      .ant-table-row:last-of-type > td {
        border-bottom: 1px solid #f0f0f0;
      }
    }
  }
}
.affiliate-info {
  margin: 20px 0;
  color: #999;
  :deep(.ant-row) {
    margin-top: 20px;
  }
  em {
    color: #000;
    font-style: normal;
  }
  .converter-info-col {
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
  }
  .text-wrap {
    display: block;
    max-width: 100%;
    word-break: break-all;
    overflow-wrap: anywhere;
    white-space: normal;
  }
}
.influencer-threshold-table {
  margin-top: 12px;
  max-width: 960px;
  :deep(.ant-table) {
    background: #fff;
  }
}
</style>